import api from './api';

/**
 * Show Notes Service - AI-powered transcription and summarization
 *
 * Enables automatic generation of:
 * - Full transcript with timestamps
 * - AI-generated summary
 * - Key topics and themes
 * - Notable quotes
 * - Chapter markers
 * - Mood/vibe classification
 */

export type ShowNotesStatus = 'pending' | 'processing' | 'ready' | 'failed';

export interface ShowNotesChapter {
  title: string;
  startTime: number;  // seconds from start
  description: string;
}

export interface ShowNotes {
  streamId: string;
  status: ShowNotesStatus;
  summary?: string;
  keyTopics?: string[];
  notableQuotes?: string[];
  mood?: string;
  tags?: string[];
  chapters?: ShowNotesChapter[];
  transcript?: string;
  duration?: number;
  createdAt: number;
  updatedAt: number;
}

export interface GenerateShowNotesOptions {
  includeTranscript?: boolean;
  includeChapters?: boolean;
}

export const showNotesService = {
  /**
   * Generate show notes for a stream
   * Requires authentication and stream ownership
   * @param streamId - The stream to generate notes for
   * @param options - Generation options
   */
  async generateShowNotes(
    streamId: string,
    options: GenerateShowNotesOptions = {}
  ): Promise<ShowNotes> {
    return api.post<ShowNotes>(`/streams/${streamId}/show-notes`, {
      includeTranscript: options.includeTranscript ?? true,
      includeChapters: options.includeChapters ?? true,
    });
  },

  /**
   * Get show notes for a stream
   * Public endpoint - anyone can view
   * @param streamId - The stream ID
   */
  async getShowNotes(streamId: string): Promise<ShowNotes | null> {
    try {
      return await api.get<ShowNotes>(`/streams/${streamId}/show-notes`);
    } catch (error: any) {
      // 404 means no show notes exist
      if (error?.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Delete show notes for a stream
   * Requires authentication and stream ownership
   * @param streamId - The stream ID
   */
  async deleteShowNotes(streamId: string): Promise<{ message: string }> {
    return api.delete<{ message: string }>(`/streams/${streamId}/show-notes`);
  },

  /**
   * Format chapter time as human-readable string
   */
  formatChapterTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  },

  /**
   * Check if show notes are available and ready
   */
  isReady(showNotes: ShowNotes | null): boolean {
    return showNotes !== null && showNotes.status === 'ready';
  },

  /**
   * Check if show notes are being processed
   */
  isProcessing(showNotes: ShowNotes | null): boolean {
    return showNotes !== null && showNotes.status === 'processing';
  },
};

export default showNotesService;
